// Pure helpers for the agent draft panel's A2 layout (AgentDraftLayout.jsx,
// 2026-10-23; docs/decisions/2026-10-23-agent-draft-panel-a2.md). One layout
// for both places the agent's draft shows: the main AI Suggestion panel when
// the agent's draft is the main one (AGENT_DRAFT_PRIMARY_INTENTS, every CSR,
// working) and the admin-only agent draft column (display only). Kept in a
// .js file so ui/test can run them under node --test.
import { isRoutineNextStep, unfilledPlaceholderFields, placeholderLines, carrierPickList, UNRESOLVED_PLACEHOLDER_SOURCE } from '@cxhub/shared'

// --- What's next, condensed ---------------------------------------------------
// One item per thing the person must do, not one per tracking number: the
// tracking numbers and links are in the reply, so What's next drops them,
// and steps that differ only by their tracking number become one ("Track the
// 3 packages on the ATS website."). A conditional rule ("If there's been no
// carrier scan for 5 or more business days ...") is kept apart, shown as one
// muted line. The routine steps (send the reply, add the note) are hidden as
// before. The Amazon carrier reads ATS here (internal).

const URL_RE = /<?https?:\/\/[^\s>]*[^\s>.,;:!?)]>?/g
// A tracking number: Amazon's TBA…, UPS's 1Z…, or 12+ digits (USPS, FedEx).
const TRACKING_RE = /\b(?:TBA\d{6,}|1Z[0-9A-Z]{16}|\d{12,34})\b/g
const SENTINEL = '§'
const CONDITION_RE = /^(?:if|when|unless|in case|should|no (?:carrier )?scan)\b/i

function stripLinks(text) {
  return String(text ?? '')
    .replace(URL_RE, '')
    .replace(/\s*:\s*(?=[.!?]?\s*$)/, '')
    .replace(/\(\s*\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([.,;:!?])/g, '$1')
    .trim()
}

// "the package" / "the 3 packages" in place of the numbers a step named
// ("label <number>" reads "the label", never "label the package").
function packagePhrase(text, count) {
  const re = new RegExp(String.raw`(?:\bthe\s+)?(?:\b(return label|label|package|tracking number)\s+)?${SENTINEL}(?:(?:\s*(?:,|and)\s*)+${SENTINEL})*`, 'gi')
  return text.replace(re, (_m, noun) => {
    const n = String(noun ?? 'package').toLowerCase()
    return count > 1 ? `the ${count} ${n}s` : `the ${n}`
  })
}

// --- A failed call, in plain words (2026-10-27) ---------------------------------
// A What's next line that carries a failed call's raw response ("Recover
// label <tracking> for <RMA> and send it (TRQ: {"error":"Internal Server
// Error",...,"status":500})", from the server's labelRecoveryStep()) becomes
// one plain line: "Label recovery for <RMA> failed in TRQ (error 500);
// recover the label in the console." Only the error's status is kept, never
// the body; lines that read the same afterwards show once. A recovery line
// is kept as written after this (its label number is never condensed).

// The status of a failed call from its logged error text, or a short plain
// message when there is no status; null when there is neither.
export function failedCallDetail(raw) {
  const t = String(raw ?? '').trim()
  if (!t) return null
  const status = /"(?:status|statusCode|code)"\s*:\s*"?(\d{3})\b/.exec(t)?.[1]
    ?? /\b(?:HTTP|status|error)\s*:?\s*([45]\d{2})\b/i.exec(t)?.[1]
    ?? null
  if (status) return `error ${status}`
  if (/^[{[]/.test(t)) {
    try {
      const body = JSON.parse(t)
      const msg = [body?.message, body?.error, body?.title].find((m) => typeof m === 'string' && m.trim() && m.length <= 80)
      return msg ? msg.trim() : 'error'
    } catch {
      return 'error'
    }
  }
  return t.length <= 80 ? t : 'error'
}

const RECOVERY_RE = /^Recover label (\S+?)(?: for (\S+?))? and send it(?: \((TRQ)(?: at generation)?: ([\s\S]*)\))?\.?$/i
const RAW_CALL_RE = /\((TRQ|eBay|Console\/OMS|OMS|Smarty|M365|Graph)(?: at generation)?:\s*([{[][\s\S]*[}\]])\)/g

// { text, kept }: kept lines are shown as written (a recovery line).
export function plainFailedCallLine(line) {
  const s = String(line ?? '').trim()
  const m = RECOVERY_RE.exec(s)
  if (m && m[4] != null) {
    const [, tracking, rma, system, raw] = m
    const detail = failedCallDetail(raw)
    return { text: `Label recovery for ${rma ?? `the return label ${tracking}`} failed in ${system}${detail ? ` (${detail})` : ''}; recover the label in the console.`, kept: true }
  }
  if (m) return { text: s, kept: true }
  const text = s.replace(RAW_CALL_RE, (_x, system, raw) => `(${system} ${failedCallDetail(raw) === 'error' ? 'call failed' : failedCallDetail(raw)})`)
  return { text, kept: false }
}

export function condenseNextSteps(steps = []) {
  const items = []
  const conditions = []
  const groups = new Map()
  const kept = []
  for (const raw of steps ?? []) {
    const plainLine = plainFailedCallLine(raw)
    const s = plainLine.text
    if (!s || isRoutineNextStep(s)) continue
    if (plainLine.kept) {
      if (!kept.includes(s)) kept.push(s)
      continue
    }
    const text = stripLinks(s.replace(/\bAMZ\b/g, 'ATS'))
    const numbers = text.match(TRACKING_RE) ?? []
    const shape = text.replace(TRACKING_RE, SENTINEL).replace(new RegExp(`${SENTINEL}(?:(?:\\s*(?:,|and)\\s*)+${SENTINEL})+`, 'g'), SENTINEL)
    const key = shape.toLowerCase()
    if (CONDITION_RE.test(shape)) {
      if (!conditions.some((c) => c.key === key)) conditions.push({ key, shape, numbers: [] })
      continue
    }
    if (groups.has(key)) {
      const g = groups.get(key)
      for (const n of numbers) if (!g.numbers.includes(n)) g.numbers.push(n)
      g.count += Math.max(1, numbers.length)
      continue
    }
    const g = { key, shape, numbers: [...new Set(numbers)], count: Math.max(1, new Set(numbers).size) }
    groups.set(key, g)
    items.push(g)
  }
  const finish = (g) => {
    const n = g.numbers.length || g.count
    return g.shape.includes(SENTINEL) ? packagePhrase(g.shape, n).replace(/\s{2,}/g, ' ').trim() : g.shape
  }
  const condensed = items.map(finish)
  return { items: [...condensed, ...kept.filter((k) => !condensed.includes(k))], conditions: conditions.map(finish) }
}

// --- The reply's placeholders ---------------------------------------------------

// "1 placeholder to fill: Estimated Delivery Date" (one line under the
// reply), or null when none is left. The fields by their line's label
// (@cxhub/shared's unfilledPlaceholderFields()); a carrier pick list as
// "Carrier".
// Every token on a line counts (two on one line are two to fill).
export function placeholderFieldNames(text) {
  const names = []
  const add = (n) => { if (n && !names.includes(n)) names.push(n) }
  let carrier = false
  const tokenRe = new RegExp(UNRESOLVED_PLACEHOLDER_SOURCE, 'g')
  for (const line of placeholderLines(text)) {
    if (carrierPickList(line)) { carrier = true; continue }
    const tokens = line.match(tokenRe) ?? []
    if (!tokens.length) {
      for (const f of unfilledPlaceholderFields(line).fields) add(f)
      continue
    }
    const label = /^([^:]{1,60}):/.exec(line)?.[1]?.trim() ?? null
    const colon = line.indexOf(':')
    for (const t of tokens) add(label && line.indexOf(t) > colon ? label : t)
  }
  if (carrier) add('Carrier')
  return names
}
export function placeholderLine(text) {
  const names = placeholderFieldNames(text)
  if (!names.length) return null
  return `${names.length} placeholder${names.length === 1 ? '' : 's'} to fill: ${names.join(', ')}`
}

// The review reason without its "fill in the placeholder" sentences (the
// line under the reply says that once): "Fill in the Estimated Delivery Date
// before sending.", "Track <n> on ATS and fill in the status and delivery
// estimate.", "Choose the carrier before sending.". null when nothing is left.
const PLACEHOLDER_REASON_RES = [
  /^Fill in (?:the )?.+ before sending\.$/i,
  /^Track .+ and fill in the status and delivery estimate\.$/i,
  /^Track the package on the carrier's website and fill in the status and delivery estimate\.$/i,
  /^Choose the carrier before sending\.$/i,
]
export function withoutPlaceholderReasons(reason) {
  const text = String(reason ?? '').trim()
  if (!text) return null
  const kept = text.split(/(?<=[.!?])\s+/).filter((s) => !PLACEHOLDER_REASON_RES.some((re) => re.test(s.trim())))
  return kept.join(' ').trim() || null
}

// --- The one button -----------------------------------------------------------
// "Process all steps and send reply", with one line under it built from the
// actual steps: "Runs step 1 (add order note), then sends the reply above to
// the customer." With no steps it reads "Send Reply" and has no line. While
// a placeholder is left or a step still needs its input, it is disabled and
// the line says what to fill first.

export const PROCESS_AND_SEND_LABEL = 'Process all steps and send reply'
export const SEND_REPLY_LABEL = 'Send Reply'

// The button's label counts the checked steps it runs (2026-10-26, the step
// checkboxes): "Process 1 step and send reply", "Process 2 steps and send
// reply"; with none, "Send Reply".
// reply false (2026-10-27): no reply to send (the eBay case waits for the
// buyer): "Process 1 step", "Process 2 steps".
export function processStepsLabel(count, { reply = true } = {}) {
  const n = Number(count) || 0
  if (n <= 0) return SEND_REPLY_LABEL
  const steps = `Process ${n} ${n === 1 ? 'step' : 'steps'}`
  return reply ? `${steps} and send reply` : steps
}

// Where "the reply above" goes, for the line under the button (2026-10-27):
// "to the customer", or "to eBay inquiry 5123456789" when it is posted to
// the case. caseLabel: @cxhub/shared's caseName() of a destination that
// posts to the case, else null.
const replyTarget = (caseLabel) => (caseLabel ? `to ${caseLabel}` : 'to the customer')

const lowerFirst = (s) => {
  const t = String(s ?? '').trim()
  if (!t) return t
  // An acronym or a code ("TRQ ...", "RMA ...") keeps its case.
  return /^[A-Z][A-Z0-9]/.test(t) ? t : t[0].toLowerCase() + t.slice(1)
}
const joinList = (parts) => (parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`)

// steps: [{ number, label, type }] in the order they run.
// reply false: the steps only ("Runs step 1 (add order note)."); caseLabel:
// the case the reply is posted to (2026-10-27).
export function stepsRunLine(steps = [], { reply = true, caseLabel = null } = {}) {
  const list = (steps ?? []).filter(Boolean)
  if (!list.length) return null
  const parts = list.map((s) => `${s.number}${s.label ? ` (${lowerFirst(s.label)})` : ''}`)
  const runs = `Runs ${list.length === 1 ? 'step' : 'steps'} ${joinList(parts)}`
  return reply ? `${runs}, then sends the reply above ${replyTarget(caseLabel)}.` : `${runs}. No reply is sent.`
}

// What a step does, for "Then this adds the order note and sends the reply."
function stepDoes(s) {
  if (s.type === 'add_order_note') return 'adds the order note'
  return `runs step ${s.number}${s.label ? ` (${lowerFirst(s.label)})` : ''}`
}

// fields: the placeholder names left; blocked: the steps' own reasons
// ("Step 2 (...) needs its input filled in first."); steps: as stepsRunLine().
export function fillFirstLine({ fields = [], blocked = [], steps = [], reply = true } = {}) {
  const first = []
  if (fields.length) first.push(`Fill in the ${joinList(fields)} first.`)
  for (const b of blocked ?? []) if (b) first.push(String(b).trim())
  if (!first.length) return null
  const does = (steps ?? []).filter(Boolean).map(stepDoes)
  if (!reply) return does.length ? `${first.join(' ')} Then this ${joinList(does)}.` : first.join(' ')
  return `${first.join(' ')} Then this ${does.length ? `${joinList(does)} and ` : ''}sends the reply.`
}

// The line with no reply and no steps (2026-10-27).
export const NOTHING_TO_SEND_LINE = 'No reply and no steps to process. Use Close - No Reply Needed.'

// The button's state: { label, line, disabled, warn }.
// reply (2026-10-27): whether there is a reply to send (false: the eBay
// case waits for the buyer; the button processes the steps only).
// caseLabel: the eBay case the reply is posted to ("eBay inquiry
// 5123456789"), named in the line under the button.
export function primaryActionState({ steps = [], fields = [], blocked = [], otherReason = null, busy = false, reply = true, caseLabel = null } = {}) {
  const hasSteps = (steps ?? []).length > 0
  const label = processStepsLabel((steps ?? []).length, { reply })
  if (!reply && !hasSteps) return { label, line: NOTHING_TO_SEND_LINE, disabled: true, warn: false }
  const fill = fillFirstLine({ fields: reply ? fields : [], blocked, steps, reply })
  if (fill) return { label, line: fill, disabled: true, warn: true }
  if (otherReason && reply) return { label, line: otherReason, disabled: true, warn: true }
  return { label, line: hasSteps ? stepsRunLine(steps, { reply, caseLabel }) : null, disabled: Boolean(busy), warn: false }
}

// --- Details (collapsed) ------------------------------------------------------

// "4 model calls, 13 s, about $0.0906, gpt-5-mini", or null.
export function runDetailsLine(run) {
  if (!run || run.rounds == null) return null
  const usd = run.estimatedUsd ?? run.usage?.estimatedUsd
  return `${run.rounds} model ${run.rounds === 1 ? 'call' : 'calls'}, ${Math.round((run.durationMs ?? 0) / 1000)} s${usd != null ? `, about $${Number(usd).toFixed(4)}` : ''}${run.model ? `, ${run.model}` : ''}`
}

export const DETAILS_LABEL = 'Details'

// --- The header (2026-10-26) ---------------------------------------------------

// "Generated 10/26/2026 11:42 AM ET": when the draft on screen was written
// (the suggestion's createdAt; the admin panel's shadow draft's), in
// America/New_York. null without a valid time.
export function generatedAtLabel(at) {
  if (!at) return null
  const d = new Date(at)
  if (Number.isNaN(d.getTime())) return null
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }).formatToParts(d).map((p) => [p.type, p.value]))
  return `Generated ${parts.month}/${parts.day}/${parts.year} ${parts.hour}:${parts.minute} ${String(parts.dayPeriod ?? '').toUpperCase()} ET`
}

export const AGENT_DRAFT_CHIP_TOOLTIP = 'This reply and its steps were written by the agent'
export const LIVE_CHIP_LABEL = 'Live'
export const LIVE_CHIP_TOOLTIP = 'The suggestion the CSR sees'

// The "Agent draft" chip's tooltip, with the run's confidence level when
// there is one: "This reply and its steps were written by the agent.
// Confidence: Medium."
export function agentDraftChipTooltip(confidenceLevel) {
  const level = String(confidenceLevel ?? '').trim().toLowerCase()
  const name = level ? level[0].toUpperCase() + level.slice(1) : ''
  return name ? `${AGENT_DRAFT_CHIP_TOOLTIP}. Confidence: ${name}.` : `${AGENT_DRAFT_CHIP_TOOLTIP}.`
}

// The "Live" chip beside the AI Suggestion title: shown only while the
// comparison panel is actually on screen beside it (side by side; not the
// collapsed rail, and, as before, not stacked under it on a narrow screen),
// and never on an agent-draft suggestion, where "Agent draft" alone says it.
// shadowMode: SuggestionPanel.jsx's shadowCompareMode().
export function showLiveChip(shadowMode, draftSource) {
  if (draftSource === 'agent') return false
  return shadowMode === 'side'
}
